import iconsPng from '@/assets/img/png/icons';
import ConfirmationModal from '@/components/molecules/ConfirmationModal';
import { PEER_SUPPORT_LEAD_UPGRADE_NOT_ALLOWED } from '@/constants/stringVars';
import usePeerSupportLeadAssign from '@/hooks/organization/peerSupportLeadAssign';
import usePeerSupportLeadRevoke from '@/hooks/organization/peerSupportLeadRevoke';
import { Image, Tooltip, VStack } from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';

interface Props {
  isPeerSupport: boolean;
  isPeerSupportLead: boolean;
  isOrgAdmin: boolean;
  isRowHovered: boolean;
  userId: string;
  userFullName?: string;
}

const PeerSupportLeadIconTrigger = ({
  isPeerSupport,
  isPeerSupportLead,
  isOrgAdmin,
  isRowHovered,
  userId,
  userFullName,
}: Props) => {
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isDowngradeModalOpen, setIsDowngradeModalOpen] = useState(false);

  const onClickIconTrigger = () => {
    if (!isPeerSupport || isOrgAdmin) return;

    if (isPeerSupportLead) {
      setIsDowngradeModalOpen(true);
    } else {
      setIsUpgradeModalOpen(true);
    }
  };

  const { mutate: assignPeerSupportLead, isPending: isPendingPeerSupportLeadAssign } = usePeerSupportLeadAssign(() =>
    setIsUpgradeModalOpen(false),
  );
  const { mutate: revokePeerSupportLead, isPending: isPendingPeerSupportLeadRevoke } = usePeerSupportLeadRevoke(() =>
    setIsDowngradeModalOpen(false),
  );

  const opacity = isPeerSupport ? (isPeerSupportLead ? 1 : isRowHovered ? 0.3 : 0) : isRowHovered ? 0.3 : 0;

  const tooltipLabel = useMemo(() => {
    if (isOrgAdmin) return `Organization Admin can't be a Peer Support Lead`;

    if (isPeerSupport) {
      if (isPeerSupportLead) {
        return 'Demote to Peer Support';
      } else {
        return 'Promote to Peer Support Lead';
      }
    } else {
      return PEER_SUPPORT_LEAD_UPGRADE_NOT_ALLOWED;
    }
  }, [isOrgAdmin, isPeerSupport, isPeerSupportLead]);

  return (
    <VStack>
      <Tooltip
        label={tooltipLabel}
        color={'text.darkBlue'}
        fontWeight={600}
        background={'white'}
        maxWidth={'140px'}
        borderRadius={'8px'}
        padding={'10px'}
        placement="bottom-start"
        left={5}
      >
        <Image
          alt="peer-support-lead"
          src={iconsPng.peerSupportLead}
          width={4}
          height={5}
          cursor={isPeerSupport && !isOrgAdmin ? 'pointer' : 'not-allowed'}
          opacity={opacity}
          _hover={{ opacity: isPeerSupportLead || isOrgAdmin ? 0.5 : 1 }}
          onClick={onClickIconTrigger}
        />
      </Tooltip>

      <ConfirmationModal
        title="Promote to Peer Support Lead"
        description={`${userFullName} will become a Peer Support Lead. Leads are responsible for managing deferred support requests from other peer support members.`}
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        confirmButtonText="Promote"
        onConfirm={() => {
          assignPeerSupportLead(userId);
        }}
        confirmLoading={isPendingPeerSupportLeadAssign}
      />

      <ConfirmationModal
        title="Demote from Peer Support Lead"
        description={`${userFullName} will no longer be a Peer Support Lead and will stop receiving deferred support requests from other members.`}
        isOpen={isDowngradeModalOpen}
        onClose={() => setIsDowngradeModalOpen(false)}
        confirmButtonColor="extra.red"
        confirmButtonText="Demote"
        onConfirm={() => {
          revokePeerSupportLead(userId);
        }}
        confirmLoading={isPendingPeerSupportLeadRevoke}
      />
    </VStack>
  );
};

export default PeerSupportLeadIconTrigger;
